/**
 * WebSocket message batching.
 *
 * Batches high-frequency entity updates into periodic flushes to reduce
 * Redux dispatch overhead and React re-renders. When the same entity
 * updates multiple times within the flush interval, only the final state
 * is dispatched (last-write-wins).
 *
 * Flush interval is 1000ms. While the tab is
 * hidden, updates keep buffering but no flush is scheduled; the buffer is
 * flushed when the tab becomes visible again.
 *
 * Routes overview for the department in TUNED_DOWN_DEPARTMENT_ID buffers for
 * TUNED_DOWN_FLUSH_SECONDS instead, except while an optimize job is running -
 * see shouldTuneDown. That interval is overridable per session with
 * ?wsBatchSeconds=.
 */

import i18next from 'i18next'
import throttle from 'lodash-es/throttle'
import { ENTITIES_RECEIVED } from '../actions/actionTypes'
import { addNotification, dismissNotification } from '../actions/creators/helpersNotifications'

const FLUSH_MS = 1_000

let buffer: Record<string, Record<string, unknown>> = {}
let throttledFlush: ReturnType<typeof throttle> | null = null
let currentConfig: { interval: number; leading: boolean } | null = null
// biome-ignore lint/suspicious/noExplicitAny: Redux store type is complex
let store: any = null

const getFlushConfig = () => ({
  interval: FLUSH_MS,
  leading: !document.hidden
})

const flush = () => {
  if (!store || Object.keys(buffer).length === 0) return
  store.dispatch({
    meta: { raf: true },
    data: buffer,
    consignmentStatesAccepted: store.getState().getIn(['context', 'consignmentStates']),
    slotIdsBypassingConsignmentStateFilter: store
      .getState()
      .getIn(['context', 'slotIdsBypassingConsignmentStateFilter']),
    type: ENTITIES_RECEIVED
  })
  buffer = {}
}

const createThrottledFlush = (config: { interval: number; leading: boolean }) =>
  throttle(flush, config.interval, {
    leading: config.leading,
    trailing: true
  })

const scheduleFlush = () => {
  const nextConfig = getFlushConfig()
  if (
    !throttledFlush ||
    !currentConfig ||
    currentConfig.interval !== nextConfig.interval ||
    currentConfig.leading !== nextConfig.leading
  ) {
    throttledFlush?.cancel()
    currentConfig = nextConfig
    throttledFlush = createThrottledFlush(currentConfig)
  }
  if (!document.hidden) {
    throttledFlush()
  }
}

const rescheduleOnVisibilityChange = () => {
  throttledFlush?.cancel()
  currentConfig = null
  if (Object.keys(buffer).length > 0) {
    scheduleFlush()
  }
  if (!store) return
  if (document.hidden) {
    store.dispatch(
      addNotification({
        id: 'live-updates-paused',
        button: 'reload',
        title: i18next.t('notifications.liveUpdatesPaused'),
        message: i18next.t('notifications.liveUpdatesDescription'),
        type: 'warning'
      })
    )
  } else {
    store.dispatch(dismissNotification('live-updates-paused'))
  }
}

let listenersAttached = false

const attachListeners = () => {
  if (listenersAttached || typeof window === 'undefined' || typeof document === 'undefined') return
  listenersAttached = true
  document.addEventListener('visibilitychange', rescheduleOnVisibilityChange)
}

/**
 * Process a WebSocket message with optional batching.
 * Returns the data to dispatch immediately, or null once it has been buffered for a later flush.
 */
// biome-ignore lint/suspicious/noExplicitAny: Redux store type is complex
export const processWsMessage = (reduxStore: any, data: unknown): unknown => {
  attachListeners()
  store = reduxStore

  if (!data || typeof data !== 'object' || Array.isArray(data)) return data

  const payload = data as Record<string, unknown>
  for (const type of Object.keys(payload)) {
    const entities = payload[type]
    if (!entities || typeof entities !== 'object' || Array.isArray(entities)) continue
    const entitiesById = entities as Record<string, unknown>
    buffer[type] ??= {}
    for (const entityId of Object.keys(entitiesById)) {
      const nextEntity = entitiesById[entityId]
      buffer[type][entityId] = nextEntity
    }
  }

  scheduleFlush()
  return null
}
